/**
 * WHEN TO TRY THE CLOUD RUNTIME AGAIN, AND HOW SOON.
 *
 * Clicking a thread whose sprite is asleep should resume it and show the thread
 * — no second click, no fifteen-second spinner. Two separate defects stopped
 * that, and both live here so the policy has one owner instead of being spread
 * across each fetch site.
 *
 * **1. Transient refusals arrived as hard failures.** CMMD renders every
 * `misconfigured` result as HTTP **409**, and this client retried only `>= 500`.
 * So "another operation is already in progress", "changed before connection
 * admission completed" and "credential rotation is in progress" — all
 * self-clearing, the last one even shipping a `Retry-After` — stopped the
 * connect dead behind a manual Retry button while the sprite sat there healthy.
 *
 * The fix is NOT a list of 409 messages or codes to treat as transient. That is
 * a hand-maintained set, and a hand-maintained set is exactly what left
 * `suspended` out of CMMD's probe-recovery statuses for three weeks: the server
 * grows a case, the client silently does not cover it, and the symptom is a
 * hang. CMMD is the only side that knows whether its own refusal is temporary,
 * so it says so in one field — `retryable: true` — and this module obeys it.
 * An **unmarked 409 stays terminal**, which is the old behaviour, so the two
 * repos can deploy in either order.
 *
 * **2. The gap between attempts was a flat 15 seconds.** A suspended Sprite
 * resumes on its next inbound request in ~0.5-2s (measured 0.646s live). One
 * transient blip therefore cost fifteen seconds of spinner on a workspace that
 * had already woken. The backoff now starts well under a second and climbs,
 * and a server `Retry-After` always wins over the guess.
 */

/** First retry gap. Below the ~0.6s a warm sprite needs, on purpose. */
export const CLOUD_RUNTIME_RETRY_BASE_DELAY_MS = 300;
export const CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS = 8;

/**
 * Ceiling for both the backoff and any `Retry-After`. The connect flight has
 * its own 5-minute abort, but one bad header asking for an hour would burn that
 * inside a single sleep with nothing on screen changing.
 */
export const CLOUD_RUNTIME_RETRY_MAX_DELAY_MS = 15_000;

function isAuthStatus(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * Did CMMD mark this response as "try again shortly"?
 *
 * Strictly `=== true`: a string `"true"`, a `1`, or any other truthy shape is a
 * malformed body, and guessing in favour of retrying turns a malformed response
 * into a request loop.
 */
function declaresRetryable(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  return (body as { readonly retryable?: unknown }).retryable === true;
}

/**
 * Whether to try this cloud-runtime request again.
 *
 * Auth is checked FIRST and unconditionally: retrying a 401/403 is a storm, not
 * a resume, and a body that claims `retryable` must never be able to start one.
 */
export function shouldRetryCloudRuntimeResponse(input: {
  readonly status: number;
  readonly body: unknown;
  readonly attempt: number;
  readonly maxAttempts: number;
}): boolean {
  if (isAuthStatus(input.status)) return false;
  if (input.attempt >= input.maxAttempts) return false;
  if (input.status >= 200 && input.status < 300) return false;
  if (input.status >= 500) return true;
  return input.status === 409 && declaresRetryable(input.body);
}

/**
 * Parse a `Retry-After` value, in either legal form: delta-seconds, or an
 * HTTP-date. Returns null when it is absent or unparseable, so the caller falls
 * back to its own backoff rather than treating a bad header as "retry now".
 */
function parseRetryAfterMs(retryAfter: string | null | undefined, nowMs: number): number | null {
  const raw = retryAfter?.trim();
  if (!raw) return null;

  // Delta-seconds. Checked before Date.parse because some engines will happily
  // parse a bare integer as a year.
  if (/^-?\d+$/.test(raw)) return Number(raw) * 1_000;

  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : at - nowMs;
}

/**
 * How long to wait before the next attempt.
 *
 * A server `Retry-After` wins — CMMD knows when its rotation ends, and guessing
 * over the top of that is strictly worse. Everything is clamped into
 * `[0, CLOUD_RUNTIME_RETRY_MAX_DELAY_MS]`: an already-elapsed deadline means go
 * now, and an absurd one must not eat the flight budget in one sleep.
 */
export function cloudRuntimeRetryDelayMs(input: {
  readonly attempt: number;
  readonly retryAfter?: string | null;
  readonly nowMs?: number;
}): number {
  const clamp = (ms: number) => Math.min(CLOUD_RUNTIME_RETRY_MAX_DELAY_MS, Math.max(0, ms));

  const serverDelayMs = parseRetryAfterMs(input.retryAfter, input.nowMs ?? Date.now());
  if (serverDelayMs !== null) return clamp(serverDelayMs);

  const exponent = Math.max(0, input.attempt - 1);
  return clamp(CLOUD_RUNTIME_RETRY_BASE_DELAY_MS * 2 ** exponent);
}
